/**
 * Models for the Assignments tab (Tab 2) of the Station Assignment workflow.
 *
 * These types mirror the backend facade response (RnRRCCFacadeService).
 */

// ─── Enums ──────────────────────────────────────────────────────────────────────

/**
 * Staffing mode for a station assignment process path.
 * Determines how associates are rendered:
 *   - PROCESS_LINES: workstation cards (lines with slots)
 *   - DEPARTMENT: associate pool read from workstations[0].assignedAssociates
 */
export const StaffingMode = {
  PROCESS_LINES: 'PROCESS_LINES',
  DEPARTMENT: 'DEPARTMENT',
} as const;
export type StaffingMode = typeof StaffingMode[keyof typeof StaffingMode];

/**
 * Path type indicates whether the function is direct or indirect.
 * Used for badge display and metrics row selection.
 */
export const PathType = {
  DIRECT: 'DIRECT',
  INDIRECT: 'INDIRECT',
} as const;
export type PathType = typeof PathType[keyof typeof PathType];

// ─── Interfaces ─────────────────────────────────────────────────────────────────

/**
 * A single associate in the assignments view.
 *
 * `assignedWorkstationId` is a convenience marker kept in sync by the
 * assignment helpers. The authoritative location for an associate is the
 * process-path / workstation structure itself (see `GetStationAssignmentsResponse`).
 */
export interface AssignmentAssociate {
  associateId: string;
  associateName: string;
  /**
   * The associate's login alias, sent by the getStationAssignment facade
   * alongside `associateName` and `associateId`. Used to construct the
   * badge photo URL (see `getBadgePhotoUrl`).
   */
  associateAlias?: string;
  avatarUrl?: string;
  isClocked: boolean;
  hasWarning: boolean;
  trainedPaths: string[];
  assignedWorkstationId?: string;
}

/**
 * A workstation (line) within a process path.
 */
export interface Workstation {
  workstationId: string;
  workstationName: string;
  assignedAssociates: AssignmentAssociate[];
  capacity: number;
}

/**
 * Per-process-path metrics row. Fields marked optional apply only to certain
 * staffing modes:
 *   - `totalFlow`, `avgRatePerHour` are used for PROCESS_LINES.
 *   - `dr`, `avgScanRate`, `unitsPerHour`, `processedPackages` are used for
 *     DEPARTMENT.
 */
export interface ProcessPathMetrics {
  totalFlow?: number;
  actual: number;
  assigned: number;
  delta: number;
  avgRatePerHour?: number;
  dr?: number;
  avgScanRate?: number;
  unitsPerHour?: number;
  processedPackages?: number;
}

/**
 * A single process path (function) in the station assignment.
 *
 * `workstations` is always present:
 *   - PROCESS_LINES: multiple workstations (lines) with slots
 *   - DEPARTMENT: single workstation whose assignedAssociates are the pool
 */
export interface ProcessPath {
  fclmFunctionId: string;
  processPathName: string;
  pathType: PathType;
  staffingMode: StaffingMode;
  metrics: ProcessPathMetrics;
  workstations: Workstation[];
  indirectAssociates?: AssignmentAssociate[];
  departmentName?: string;
  processPathMappingName?: string;
  fclmProcessPathId?: number;
}

/**
 * Aggregate metrics displayed in the top-level MetricsBar.
 */
export interface AssignmentsMetrics {
  totalUnlocked: number;
  totalClockedIn: number;
  totalActual: number;
  assignedPrimary: number;
  assignedProblemSolve: number;
  assignedResale: number;
  assignedCleanDecant: number;
  assignedIndirect: number;
}

/**
 * Request payload for the `/getStationAssignment` endpoint.
 */
export interface GetStationAssignmentsRequest {
  nodeId: string;
  warehouseId: string;
  ofdDate: string;
  clockWindowName: string;
  planId: string;
}

/**
 * Data for a single half of the shift.
 */
export interface StationAssignmentHalfData {
  associates: AssignmentAssociate[];
  processPaths: ProcessPath[];
  metrics: AssignmentsMetrics;
}

/**
 * Response from the `/getStationAssignment` endpoint.
 *
 * Contains assignment data for both halves of the shift. The UI selects
 * the active half based on the half selector without re-fetching.
 */
export interface GetStationAssignmentsResponse extends StationAssignmentPlan {
  lastModifiedBy: string;
  lastModifiedDate: string;
}

export interface StationAssignmentResponse extends  StationAssignmentPlan {
  lastModifiedBy: string;
  lastModifiedDate: string;
}

/**
 * Station assignment data structure used in the publish request payload.
 * Structurally identical to GetStationAssignmentsResponse but named to
 * reflect its role as the submission payload rather than a GET response.
 */
export interface StationAssignmentPlan {
  firstHalfStationAssignment: StationAssignmentHalfData;
  secondHalfStationAssignment: StationAssignmentHalfData;
}

/**
 * Empty half data used as the default/error fallback.
 */
const EMPTY_HALF: StationAssignmentHalfData = {
  associates: [],
  processPaths: [],
  metrics: {
    totalUnlocked: 0,
    totalClockedIn: 0,
    totalActual: 0,
    assignedPrimary: 0,
    assignedProblemSolve: 0,
    assignedResale: 0,
    assignedCleanDecant: 0,
    assignedIndirect: 0,
  },
};

/**
 * Empty response used as the default/error fallback in `api.ts`.
 */
export const EMPTY_RESPONSE: GetStationAssignmentsResponse = {
  firstHalfStationAssignment: EMPTY_HALF,
  secondHalfStationAssignment: EMPTY_HALF,
  lastModifiedBy: '',
  lastModifiedDate: '',
};

// ─── IsValidOverride API Types ──────────────────────────────────────────────────

/**
 * A single associate override entry for validation.
 */
export interface AssociateOverrideEntry {
  associateId: string;
  fclmFunctionId: string;
  fclmProcessPathId: number;
  targetWorkstationId: string;
}

/**
 * Request payload for the `/isValidStationAssignmentOverride` endpoint.
 * Contains 1 entry for a single move, 2 entries for a swap.
 */
export interface IsValidStationAssignmentOverrideRequest {
  nodeId: string;
  warehouseId: string;
  planId: string;
  overrides: AssociateOverrideEntry[];
}

/**
 * Response from the `/isValidStationAssignmentOverride` endpoint.
 */
export interface IsValidStationAssignmentOverrideResponse {
  validationErrors: string[];
}

/**
 * Request payload for the `/publishStationAssignment` endpoint.
 * Contains the plan metadata and the full assignment state (with all swaps applied).
 */
export interface PublishStationAssignmentRequest {
  planId: string;
  nodeId: string;
  warehouseId: string;
  updatedBy: string;
  stationAssignment: StationAssignmentResponse;
}

// ─── SearchAssociateModal Types ─────────────────────────────────────────────────

/**
 * Context passed to SearchAssociateModal describing the target workstation
 * and its parent process path.
 */
export interface AssignModalTarget {
  workstationId: string;
  workstationName: string;
  fclmFunctionId: string;
  fclmProcessPathId: number | null;
}

export interface SearchAssociateModalProps {
  open: boolean;
  onClose: () => void;
  associates: AssignmentAssociate[];
  processPaths: ProcessPath[];
  onSubmit: (associateId: string) => void;
  target: AssignModalTarget;
  /** When set, the modal operates in swap mode showing two pre-selected associates */
  swapAssociates?: [AssignmentAssociate, AssignmentAssociate];
}
